/**
 * Registre de contenu dynamique — source unique de vérité runtime.
 *
 * Fusionne les données de base (hardcodées) avec les overrides admin stockés
 * dans state.customContent. Permet un CRUD complet via l'admin sans toucher
 * aux fichiers source.
 *
 * Architecture :
 *   - BASE : deepClone des imports statiques (référence immutable)
 *   - REGISTRIES : objet Vue reactive fusionné (base + custom + générés)
 *   - state.customContent : persistance locale des overrides
 */

import { reactive, watch } from 'vue';
import { CLASSES, ITEMS, FAMILIAR_SKILLS } from '../domain/progression/items.js';
import { SKILL_TREES } from '../domain/combat/skillTrees.js';
import { LIFE_SPELLS, findSkillAnywhere } from '../domain/progression/lifeTree.js';
import { TITLES } from '../domain/progression/titles.js';
import { STATUS_EFFECTS } from '../domain/combat/statusEffects.js';
import { ELEMENTS } from '../domain/combat/elements.js';
import { RETIRED_LYRA_DERIVED_ADVENTURER_IDS } from '../shared/lyraStarterOutfit';
import { resolvePublicAssetUrl } from '../shared/publicAssets';

let state: any = null;

// =============================================================================
// UTILITAIRES
// =============================================================================

function deepClone(obj: any): any {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(deepClone);
  const clone: Record<string, any> = {};
  for (const [k, v] of Object.entries(obj)) {
    clone[k] = deepClone(v);
  }
  return clone;
}

function ensureArray(arr: any) {
  return Array.isArray(arr) ? arr : [];
}

function generateId(prefix = '') {
  return `${prefix}${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

// =============================================================================
// AVATARS GÉNÉRÉS
// =============================================================================

function generateAvatarItems(classes: any) {
  const avatars: Record<string, any> = {};
  const genders = { h: 'Homme', f: 'Femme' };
  for (const [gId, gLabel] of Object.entries(genders)) {
    for (const cId of Object.keys(classes)) {
      const cLabel = classes[cId]?.label || cId;
      const baseId = `avatar_${cId}_${gId}`;
      avatars[baseId] = {
        id: baseId,
        cat: 'avatar_skin',
        slot: 'avatarDataUrl',
        name: `Avatar ${cLabel} ${gLabel}`,
        price: 500,
        rarity: 'rare',
        desc: 'Une apparence pour votre personnage.',
        img: resolvePublicAssetUrl(`/assets/avatars/${baseId}.png`)
      };
      for (let i = 1; i <= 4; i++) {
        const varId = `${baseId}_${i}`;
        avatars[varId] = {
          id: varId,
          cat: 'avatar_skin',
          slot: 'avatarDataUrl',
          name: `Avatar ${cLabel} ${gLabel} (Var ${i})`,
          price: 500,
          rarity: 'rare',
          desc: 'Une apparence alternative.',
          img: resolvePublicAssetUrl(`/assets/avatars/${varId}.webp`)
        };
      }
    }
  }
  return avatars;
}

// =============================================================================
// BASE DE DONNÉES
// =============================================================================

export const BASE = {
  classes: deepClone(CLASSES),
  items: { ...deepClone(ITEMS), ...generateAvatarItems(CLASSES) },
  skillTrees: deepClone(SKILL_TREES),
  titles: deepClone(TITLES),
  familiarSkills: deepClone(FAMILIAR_SKILLS),
  statusEffects: deepClone(STATUS_EFFECTS),
  elements: deepClone(ELEMENTS)
};

// =============================================================================
// REGISTRES RÉACTIFS
// =============================================================================

export const REGISTRIES = reactive({
  classes: {} as Record<string, any>,
  items: {} as Record<string, any>,
  skillTrees: {} as Record<string, any>,
  titles: {} as Record<string, any>,
  familiarSkills: {} as Record<string, any>,
  statusEffects: {} as Record<string, any>,
  elements: {} as Record<string, any>
});

// =============================================================================
// FUSION BASE + CUSTOM
// =============================================================================

function mergeRegistry(base: any, custom: any) {
  const result = deepClone(base);
  for (const [id, value] of Object.entries(custom || {})) {
    if (value === null || value === false) {
      delete result[id];
    } else if (typeof value === 'object') {
      // Fusion object : ne pas écraser les propriétés de base avec `undefined`
      // (évite que des overrides admin partiels ne corrompent les champs obligatoires).
      result[id] = { ...result[id] };
      for (const [k, v] of Object.entries(value)) {
        if (v !== undefined) result[id][k] = v;
      }
    } else {
      result[id] = value;
    }
  }
  return result;
}

function mergeSkillTrees(base: any, custom: any) {
  const result = deepClone(base);
  for (const [classId, customTree] of Object.entries(custom || {})) {
    if (customTree === null || customTree === false) {
      delete result[classId];
      continue;
    }
    if (!Array.isArray(customTree)) continue;
    const baseTree = result[classId] || [];
    const mergedTree = [...baseTree];
    for (const patch of customTree) {
      if (!patch?.id) continue;
      const idx = mergedTree.findIndex((s: any) => s.id === patch.id);
      if (patch._deleted) {
        if (idx > -1) mergedTree.splice(idx, 1);
      } else if (idx > -1) {
        mergedTree[idx] = { ...mergedTree[idx], ...patch };
      } else {
        mergedTree.push(patch);
      }
    }
    result[classId] = mergedTree;
  }
  return result;
}

function mergeFamiliarSkills(base: any, custom: any) {
  const result = deepClone(base);
  for (const [familiarId, customSkills] of Object.entries(custom || {})) {
    if (customSkills === null || customSkills === false) {
      delete result[familiarId];
      continue;
    }
    if (!Array.isArray(customSkills)) continue;
    const baseSkills = result[familiarId] || [];
    const merged = [...baseSkills];
    for (const patch of customSkills) {
      if (!patch?.id) continue;
      const idx = merged.findIndex((s: any) => s.id === patch.id);
      if (patch._deleted) {
        if (idx > -1) merged.splice(idx, 1);
      } else if (idx > -1) {
        merged[idx] = { ...merged[idx], ...patch };
      } else {
        merged.push(patch);
      }
    }
    result[familiarId] = merged;
  }
  return result;
}

export function syncRegistries() {
  const customItems = { ... (state.customContent?.items || {}) };
  for (const itemId of RETIRED_LYRA_DERIVED_ADVENTURER_IDS) delete customItems[itemId];
  const custom = { ... (state.customContent || {}), items: customItems };
  REGISTRIES.classes = mergeRegistry(BASE.classes, custom.classes);
  REGISTRIES.items = mergeRegistry(BASE.items, custom.items);
  REGISTRIES.skillTrees = mergeSkillTrees(BASE.skillTrees, custom.skillTrees);
  REGISTRIES.titles = mergeRegistry(BASE.titles, custom.titles);
  REGISTRIES.familiarSkills = mergeFamiliarSkills(BASE.familiarSkills, custom.familiarSkills);
  REGISTRIES.statusEffects = mergeRegistry(BASE.statusEffects, custom.statusEffects);
  REGISTRIES.elements = mergeRegistry(BASE.elements, custom.elements);
}

// =============================================================================
// PERSISTENCE (state.customContent)
// =============================================================================

function ensureCustomContent() {
  if (!state.customContent) state.customContent = {};
  if (!state.customContent.classes) state.customContent.classes = {};
  if (!state.customContent.items) state.customContent.items = {};
  if (!state.customContent.skillTrees) state.customContent.skillTrees = {};
  if (!state.customContent.titles) state.customContent.titles = {};
  if (!state.customContent.familiarSkills) state.customContent.familiarSkills = {};
  if (!state.customContent.statusEffects) state.customContent.statusEffects = {};
  if (!state.customContent.elements) state.customContent.elements = {};
}

function setCustomPath(path: string, value: any) {
  ensureCustomContent();
  const keys = path.split('.');
  let obj = state.customContent;
  for (let i = 0; i < keys.length - 1; i++) {
    if (!obj[keys[i]] || typeof obj[keys[i]] !== 'object') obj[keys[i]] = {};
    obj = obj[keys[i]];
  }
  obj[keys[keys.length - 1]] = value;
}

function deleteCustomPath(path: string) {
  setCustomPath(path, null);
}

// =============================================================================
// CRUD — ITEMS
// =============================================================================

export function getItem(id: string) { return REGISTRIES.items[id]; }
export function listItems() { return Object.values(REGISTRIES.items); }
export function getItemsByCategory(cat: string) { return listItems().filter(i => i.cat === cat); }

export function addItem(item: any) {
  if (!item?.id) return { success: false, error: 'ID requis' };
  if (!isValidItem(item)) return { success: false, error: 'Item invalide' };
  setCustomPath(`items.${item.id}`, { ...item });
  return { success: true };
}

export function updateItem(id: string, patch: any) {
  if (!id) return { success: false, error: 'ID requis' };
  const current = REGISTRIES.items[id];
  if (!current && !patch) return { success: false, error: 'Item non trouvé' };
  const next = { ...(current || {}), ...patch, id };
  if (!isValidItem(next)) return { success: false, error: 'Item invalide' };
  setCustomPath(`items.${id}`, next);
  return { success: true };
}

export function deleteItem(id: string) {
  if (!id) return { success: false, error: 'ID requis' };
  if (isItemReferenced(id)) {
    return { success: false, error: 'Cet item est référencé par un titre (unlockShop) ou équipé. Supprimez d’abord les dépendances.' };
  }
  deleteCustomPath(`items.${id}`);
  return { success: true };
}

function isValidItem(item: any) {
  if (!item || typeof item.id !== 'string' || typeof item.name !== 'string' || !item.cat) return false;
  const allowedCats = ITEM_CATEGORIES.map(c => c.id);
  if (!allowedCats.includes(item.cat)) return false;
  if (item.rarity && !RARITIES.includes(item.rarity)) return false;
  const levelReq = Number(item.levelReq ?? 1);
  const price = Number(item.price ?? 0);
  if (!Number.isFinite(levelReq) || levelReq < 1) return false;
  if (!Number.isFinite(price) || price < 0) return false;
  if (item.cat === 'consommable' && item.combat) {
    if (typeof item.combat !== 'object' || !['heal', 'mp', 'stamina', 'revive', 'skill'].includes(item.combat.effect)) return false;
    if (item.combat.effect === 'skill' && !item.combat.skillId) return false;
    if (item.combat.effect !== 'skill' && (!Number.isFinite(Number(item.combat.value)) || Number(item.combat.value) <= 0)) return false;
    if (item.combat.effect === 'revive' && Number(item.combat.value) > 1) return false;
  }
  return true;
}

// =============================================================================
// CRUD — CLASSES
// =============================================================================

export function getClass(id: string) { return REGISTRIES.classes[id]; }
export function listClasses() { return Object.values(REGISTRIES.classes); }

export function addClass(classe: any) {
  if (!classe?.id) return { success: false, error: 'ID requis' };
  if (!isValidClass(classe)) return { success: false, error: 'Classe invalide' };
  setCustomPath(`classes.${classe.id}`, { ...classe });
  // Crée automatiquement un arbre de compétences vide pour la nouvelle classe
  if (!state.customContent.skillTrees[classe.id]) {
    state.customContent.skillTrees[classe.id] = [];
  }
  return { success: true };
}

export function updateClass(id: string, patch: any) {
  if (!id) return { success: false, error: 'ID requis' };
  const current = REGISTRIES.classes[id];
  const next = { ...(current || {}), ...patch, id };
  if (!isValidClass(next)) return { success: false, error: 'Classe invalide' };
  setCustomPath(`classes.${id}`, next);
  return { success: true };
}

export function deleteClass(id: string) {
  if (!id) return { success: false, error: 'ID requis' };
  if (REGISTRIES.skillTrees[id]?.length) {
    return { success: false, error: 'Cette classe possède encore des compétences. Supprimez-les d’abord.' };
  }
  if (id === 'guerrier') return { success: false, error: 'La classe guerrier est requise par le système.' };
  deleteCustomPath(`classes.${id}`);
  return { success: true };
}

function isValidClass(classe: any) {
  return classe && typeof classe.id === 'string' && typeof classe.label === 'string' && classe.baseStats;
}

// =============================================================================
// CRUD — COMPÉTENCES
// =============================================================================

export function getSkill(classId: string, skillId: string) {
  // Arbre Mycélien : un sort débloqué peut venir d'une AUTRE école que la classe
  // active (déblocage par stats/affinités/titres). Fallback : chercher partout.
  const own = REGISTRIES.skillTrees[classId]?.find((s: any) => s.id === skillId);
  if (own) return own;
  for (const tree of Object.values(REGISTRIES.skillTrees)) {
    const found = (tree as any[]).find((s: any) => s.id === skillId);
    if (found) return found;
  }
  return findSkillAnywhere(skillId, REGISTRIES.skillTrees);
}

export function listSkills(classId: string) {
  return classId ? ensureArray(REGISTRIES.skillTrees[classId]) : Object.values(REGISTRIES.skillTrees).flat();
}

/** Arbre Mycélien : TOUS les sorts connus (écoles de classes + Rameaux de Vie). */
export function listAllKnownSkills() {
  return [...Object.values(REGISTRIES.skillTrees).flat(), ...LIFE_SPELLS];
}

export function addSkill(classId: string, skill: any) {
  if (!classId || !skill?.id) return { success: false, error: 'classId et skill.id requis' };
  const current = REGISTRIES.skillTrees[classId]?.find((entry: any) => entry.id === skill.id);
  const next = { ...(current || {}), ...skill, id: skill.id };
  if (!isValidSkill(next)) return { success: false, error: 'Compétence invalide' };

  const owner = findSkillOwner(skill.id);
  if (owner && owner.classId !== classId) {
    return { success: false, error: `Cet ID appartient déjà à la classe ${owner.classId}.` };
  }
  if (LIFE_SPELLS.some(entry => entry.id === skill.id)) {
    return { success: false, error: 'Cet ID est réservé à un Rameau de Vie.' };
  }
  if (next.req && !getSkill(classId, next.req)) {
    return { success: false, error: `Prérequis introuvable : ${next.req}` };
  }
  if (wouldCreateSkillCycle(next)) {
    return { success: false, error: 'Ce prérequis créerait une boucle entre compétences.' };
  }

  ensureCustomContent();
  if (!state.customContent.skillTrees[classId]) state.customContent.skillTrees[classId] = [];
  const arr = state.customContent.skillTrees[classId];
  const existing = arr.find((s: any) => s.id === skill.id);
  if (existing) Object.assign(existing, next);
  else arr.push(next);
  return { success: true };
}

export function updateSkill(classId: string, skillId: string, patch: any) {
  return addSkill(classId, { ...patch, id: skillId });
}

export function deleteSkill(classId: string, skillId: string) {
  const dependents = getSkillDependents(classId, skillId);
  if (dependents.length) {
    return { success: false, error: `Compétence requise par : ${dependents.map(d => d.name).join(', ')}` };
  }
  if (!REGISTRIES.skillTrees[classId]?.some((skill: any) => skill.id === skillId)) {
    return { success: false, error: 'Compétence introuvable' };
  }
  ensureCustomContent();
  if (!state.customContent.skillTrees[classId]) state.customContent.skillTrees[classId] = [];
  const arr = state.customContent.skillTrees[classId];
  const existing = arr.find((skill: any) => skill.id === skillId);
  if (existing) Object.assign(existing, { id: skillId, _deleted: true });
  else arr.push({ id: skillId, _deleted: true });
  return { success: true };
}

function isValidSkill(skill: any) {
  if (!skill || typeof skill.id !== 'string' || typeof skill.name !== 'string' || !skill.type) return false;
  if (!SKILL_TYPES.includes(skill.type)) return false;
  if (skill.target && !SKILL_TARGETS.includes(skill.target)) return false;
  if (skill.branch && !SKILL_BRANCHES.includes(skill.branch)) return false;
  if (skill.cost != null && (typeof skill.cost !== 'object' || Array.isArray(skill.cost))) return false;
  if (skill.cost && ['mp', 'stamina'].some(key => skill.cost[key] != null && (!Number.isFinite(Number(skill.cost[key])) || Number(skill.cost[key]) < 0))) return false;
  if (skill.affinity != null && (typeof skill.affinity !== 'object' || Array.isArray(skill.affinity))) return false;
  const minLevel = Number(skill.minLevel ?? 1);
  const cooldown = Number(skill.cooldown ?? 0);
  if (!Number.isFinite(minLevel) || minLevel < 1) return false;
  if (!Number.isFinite(cooldown) || cooldown < 0) return false;
  if (skill.affinity && Object.values(skill.affinity).some(value => !Number.isFinite(Number(value)))) return false;
  if (skill.statuses && (!Array.isArray(skill.statuses) || skill.statuses.some((status: any) => {
    const chance = Number(status?.chance ?? 1);
    const duration = Number(status?.duration ?? 1);
    return !status?.id || !Number.isFinite(chance) || chance < 0 || chance > 1 || !Number.isFinite(duration) || duration <= 0;
  }))) return false;
  if (skill.type === 'revive' && (!Number.isFinite(Number(skill.effect?.pctHp)) || Number(skill.effect?.pctHp) <= 0 || Number(skill.effect?.pctHp) > 1)) return false;
  if (skill.type === 'barrier' && skill.effect?.flat != null && (!Number.isFinite(Number(skill.effect.flat)) || Number(skill.effect.flat) < 0)) return false;
  return true;
}

export function getSkillDependents(_classId: string, skillId: string) {
  const deps = [];
  for (const [dependentClassId, tree] of Object.entries(REGISTRIES.skillTrees)) {
    for (const skill of tree as any[]) {
      if (skill.req === skillId) deps.push({ ...skill, classId: dependentClassId });
    }
  }
  return deps;
}

function findSkillOwner(skillId: string) {
  for (const [classId, tree] of Object.entries(REGISTRIES.skillTrees)) {
    if ((tree as any[]).some(skill => skill.id === skillId)) return { classId };
  }
  return null;
}

function wouldCreateSkillCycle(nextSkill: any) {
  const prerequisiteBySkillId = new Map<string, string>();
  for (const tree of Object.values(REGISTRIES.skillTrees)) {
    for (const skill of tree as any[]) {
      if (skill.id && skill.req) prerequisiteBySkillId.set(skill.id, skill.req);
    }
  }
  // `nextSkill` remplace le nœud courant avant la détection, même si le
  // registre réactif n'a pas encore été synchronisé par le watcher Vue.
  if (nextSkill.req) prerequisiteBySkillId.set(nextSkill.id, nextSkill.req);
  else prerequisiteBySkillId.delete(nextSkill.id);

  const visited = new Set<string>();
  let current: string | undefined = nextSkill.id;
  while (current) {
    if (visited.has(current)) return true;
    visited.add(current);
    current = prerequisiteBySkillId.get(current);
  }
  return false;
}

// =============================================================================
// CRUD — TITRES
// =============================================================================

export function getTitle(id: string) { return REGISTRIES.titles[id]; }
export function listTitleRegistry() { return Object.values(REGISTRIES.titles); }

export function addTitle(title: any) {
  if (!title?.id) return { success: false, error: 'ID requis' };
  if (!isValidTitle(title)) return { success: false, error: 'Titre invalide' };
  setCustomPath(`titles.${title.id}`, { ...title });
  return { success: true };
}

export function updateTitle(id: string, patch: any) {
  if (!id) return { success: false, error: 'ID requis' };
  const current = REGISTRIES.titles[id];
  const next = { ...(current || {}), ...patch, id };
  if (!isValidTitle(next)) return { success: false, error: 'Titre invalide' };
  setCustomPath(`titles.${id}`, next);
  return { success: true };
}

export function deleteTitle(id: string) {
  if (!id) return { success: false, error: 'ID requis' };
  const itemsRef = getItemsRequiringTitle(id);
  if (itemsRef.length) {
    return { success: false, error: `Titre requis par les items : ${itemsRef.map(i => i.name).join(', ')}` };
  }
  deleteCustomPath(`titles.${id}`);
  return { success: true };
}

function isValidTitle(title: any) {
  return title && typeof title.id === 'string' && typeof title.name === 'string';
}

export function normalizeConditions(conditions: any) {
  if (!conditions) return [];
  if (Array.isArray(conditions)) return conditions;
  return [conditions];
}

// =============================================================================
// CRUD — FAMILIERS SKILLS
// =============================================================================

export function getFamiliarSkill(familiarId: string, skillId: string) {
  return REGISTRIES.familiarSkills[familiarId]?.find((s: any) => s.id === skillId) || null;
}

export function addFamiliarSkill(familiarId: string, skill: any) {
  if (!familiarId || !skill?.id) return { success: false, error: 'familiarId et skill.id requis' };
  ensureCustomContent();
  if (!state.customContent.familiarSkills[familiarId]) state.customContent.familiarSkills[familiarId] = [];
  const arr = state.customContent.familiarSkills[familiarId];
  const existing = arr.find((s: any) => s.id === skill.id);
  if (existing) Object.assign(existing, skill);
  else arr.push({ ...skill });
  return { success: true };
}

export function updateFamiliarSkill(familiarId: string, skillId: string, patch: any) {
  return addFamiliarSkill(familiarId, { ...patch, id: skillId });
}

export function deleteFamiliarSkill(familiarId: string, skillId: string) {
  return addFamiliarSkill(familiarId, { id: skillId, _deleted: true });
}

// =============================================================================
// CRUD — ALTÉRATIONS
// =============================================================================

export function getStatusEffectRegistry(id: string) { return REGISTRIES.statusEffects[id]; }
export function listStatusEffects() { return Object.values(REGISTRIES.statusEffects); }

export function addStatusEffect(effect: any) {
  if (!effect?.id) return { success: false, error: 'ID requis' };
  if (!isValidStatusEffect(effect)) return { success: false, error: 'Altération invalide' };
  setCustomPath(`statusEffects.${effect.id}`, { ...effect });
  return { success: true };
}

export function updateStatusEffect(id: string, patch: any) {
  if (!id) return { success: false, error: 'ID requis' };
  const current = REGISTRIES.statusEffects[id];
  const next = { ...(current || {}), ...patch, id };
  if (!isValidStatusEffect(next)) return { success: false, error: 'Altération invalide' };
  setCustomPath(`statusEffects.${id}`, next);
  return { success: true };
}

export function deleteStatusEffect(id: string) {
  if (!id) return { success: false, error: 'ID requis' };
  const dependents = getStatusEffectDependents(id);
  if (dependents.length) {
    return { success: false, error: `Altération utilisée par : ${dependents.map(d => `${d.classId}.${d.skillId}`).join(', ')}` };
  }
  deleteCustomPath(`statusEffects.${id}`);
  return { success: true };
}

function isValidStatusEffect(effect: any) {
  if (!effect || typeof effect.id !== 'string' || typeof effect.name !== 'string' || !effect.type) return false;
  if (!STATUS_TYPES.includes(effect.type)) return false;
  return true;
}

export function getStatusEffectDependents(statusId: string) {
  const deps = [];
  for (const [classId, tree] of Object.entries(REGISTRIES.skillTrees)) {
    for (const skill of tree) {
      if (skill.statuses?.some((s: any) => s.id === statusId)) {
        deps.push({ classId, skillId: skill.id, name: skill.name });
      }
    }
  }
  return deps;
}

// =============================================================================
// CRUD — ÉLÉMENTS
// =============================================================================

export function getElementRegistry(id: string) { return REGISTRIES.elements[id]; }
export function listElements() { return Object.values(REGISTRIES.elements); }

export function addElement(element: any) {
  if (!element?.id) return { success: false, error: 'ID requis' };
  if (!isValidElement(element)) return { success: false, error: 'Élément invalide' };
  setCustomPath(`elements.${element.id}`, { ...element });
  return { success: true };
}

export function updateElement(id: string, patch: any) {
  if (!id) return { success: false, error: 'ID requis' };
  const current = REGISTRIES.elements[id];
  const next = { ...(current || {}), ...patch, id };
  if (!isValidElement(next)) return { success: false, error: 'Élément invalide' };
  setCustomPath(`elements.${id}`, next);
  return { success: true };
}

export function deleteElement(id: string) {
  if (!id) return { success: false, error: 'ID requis' };
  const dependents = getElementDependents(id);
  if (dependents.length) {
    return { success: false, error: `Élément utilisé par : ${dependents.map(d => `${d.classId}.${d.skillId}`).join(', ')}` };
  }
  deleteCustomPath(`elements.${id}`);
  return { success: true };
}

function isValidElement(element: any) {
  return element && typeof element.id === 'string' && typeof element.name === 'string';
}

export function getElementDependents(elementId: string) {
  const deps = [];
  for (const [classId, tree] of Object.entries(REGISTRIES.skillTrees)) {
    for (const skill of tree) {
      if (skill.element === elementId) deps.push({ classId, skillId: skill.id, name: skill.name });
    }
  }
  return deps;
}

// =============================================================================
// HELPERS DE RÉFÉRENCEMENT
// =============================================================================

export function isItemReferenced(itemId: string) {
  for (const title of listTitleRegistry()) {
    if (title.rewards?.unlockShop?.includes(itemId)) return true;
  }
  return false;
}

export function getItemsRequiringTitle(titleId: string) {
  return listItems().filter(i => i.unlockTitle === titleId);
}

export function getTitleDependencies(itemId: string) {
  return listTitleRegistry()
    .filter(t => t.rewards?.unlockShop?.includes(itemId))
    .map(t => ({ type: 'title', id: t.id, name: t.name }));
}

export const ITEM_CATEGORIES = [
  { id: 'arme', label: 'Arme', slot: 'main_hand' },
  { id: 'armure', label: 'Armure' },
  { id: 'consommable', label: 'Consommable' },
  { id: 'familiar', label: 'Familier', slot: 'familiar' },
  { id: 'lyra', label: 'Dressing Lyra' },
  { id: 'frame', label: 'Cadre Avatar', slot: 'avatar_frame' },
  { id: 'taverne_bg', label: 'Décor Taverne', slot: 'taverne_bg' },
  { id: 'avatar_skin', label: 'Avatar', slot: 'avatarDataUrl' },
  { id: 'particles', label: 'Particules', slot: 'particles' },
  { id: 'theme', label: 'Thème', slot: 'theme' }
];

export const ITEM_SLOTS = {
  arme: 'main_hand',
  armure: 'dynamic',
  consommable: 'consumable',
  familiar: 'familiar',
  lyra: 'dress',
  frame: 'avatar_frame',
  taverne_bg: 'taverne_bg',
  avatar_skin: 'avatarDataUrl',
  particles: 'particles',
  theme: 'theme'
};

export const RARITIES = ['common', 'rare', 'epic', 'legendary'];
export const SKILL_TYPES = ['attack', 'aoe', 'heal', 'buff', 'debuff', 'dot', 'barrier', 'revive'];
export const SKILL_TARGETS = ['single', 'ally', 'allAllies', 'allEnemies', 'self'];
export const SKILL_BRANCHES = ['offense', 'defense', 'support', 'utility', 'control'];
export const STATUS_TYPES = ['buff', 'debuff'];

export function slotForCategory(cat: string) {
  return (ITEM_SLOTS as Record<string, string>)[cat] || 'misc';
}

export function getItemCategories() {
  return ITEM_CATEGORIES;
}

export function getRarities() {
  return RARITIES;
}

export function getSkillTypes() {
  return SKILL_TYPES;
}

export function getSkillTargets() {
  return SKILL_TARGETS;
}

export function getSkillBranches() {
  return SKILL_BRANCHES;
}

export function getStatusTypes() {
  return STATUS_TYPES;
}

export function getItemById(id: string) {
  return REGISTRIES.items[id];
}

export function getTitleByRegistryId(id: string) {
  return REGISTRIES.titles[id];
}

export function getClassById(id: string) {
  return REGISTRIES.classes[id];
}

export function getElementById(id: string) {
  return REGISTRIES.elements[id];
}

export function getStatusEffectById(id: string) {
  return REGISTRIES.statusEffects[id];
}

// =============================================================================
// IMPORT / EXPORT ADMIN
// =============================================================================

export function exportCustomContent() {
  return JSON.stringify(state.customContent || {}, null, 2);
}

export function importCustomContent(json: string) {
  try {
    const parsed = JSON.parse(json);
    state.customContent = parsed;
    syncRegistries();
    return { success: true };
  } catch (e: any) {
    return { success: false, error: e.message };
  }
}

export function resetCustomContent() {
  state.customContent = {};
  syncRegistries();
}

// =============================================================================
// WATCH + INIT
// =============================================================================

export function initContentRegistry(playerState: any) {
  state = playerState;
  ensureCustomContent();
  syncRegistries();
  watch(() => state.customContent, syncRegistries, { deep: true });
}
